<!--
  About page. Lives at /about for now (no nav link). Explains the
  motivation, methodology, and roadmap. Kept text-only — no charts or
  data fetches — so it stays fast to load and easy to update.
-->
<script setup lang="ts">
</script>

<template>
  <div class="about">
    <header class="about-hero">
      <div class="hero-eyebrow">About</div>
      <h1>What the data actually shows.</h1>
      <p class="hero-sub">
        Powerlifting Trends exists because the conversation around the sport runs on Instagram clips
        and locker-room takes. The data tells a more interesting story.
      </p>
    </header>

    <section class="prose-section">
      <h2 class="section-h">Our Mission</h2>
      <p>
        We're building Powerlifting Trends to put real data analysis directly into the hands of
        lifters and coaches: cohort-aware analysis that answers the questions people actually have,
        rather than aggregated leaderboards or equipment-mixed top-N lists.
      </p>
      <p>
        The goal is simple. When a lifter looks at their own progress, or a coach plans a programming
        block, they can ground their decisions in what tens of thousands of comparable
        lifters have actually done, rather than what a single social media champion claims worked for them.
      </p>
    </section>

    <section class="prose-section">
      <h2 class="section-h">In-depth comparisons</h2>
      <p>
        New lifters get a skewed picture of what "normal" looks like. Social media algorithms are
        tuned to surface outliers, so the 14-year-old hitting a 700kg total goes viral while the
        median 90kg-class progression (the path 95% of lifters will actually live through) gets buried.
      </p>
      <p>
        Career Arc and Lifter Lookup are designed specifically to push back on this. You see
        the typical Dots curve from debut through meet 30+ for your exact slice (event, equipment,
        weight class, sex), with p25/p75 bands so the realistic spread is visible. When you compare
        your own debut, you're comparing it against other debuts, rather than the seasoned lifters whose meet
        videos dominate your feed.
      </p>
    </section>

    <section class="prose-section">
      <h2 class="section-h">Where the data comes from</h2>
      <p>
        Every entry on the site is sourced from the
        <a href="https://www.openpowerlifting.org" target="_blank" rel="noopener">OpenPowerlifting</a>
        project's bulk CSV release. We pull the dataset, run it through a multi-stage ingest
        pipeline (deduping lifters, normalizing meet metadata, computing career trajectories), and
        store it in our own database.
      </p>
      <p>
        On top of OpenPowerlifting's raw data we expand with additional information we believe is
        helpful to lifters: career trajectories, percentile ranks within slice, debut-to-current
        deltas, and cohort comparisons keyed on the specifics of your event, equipment, weight
        class, and sex. The API sits behind CloudFront with query-aware caching, so the same
        cohort question hit twice in an hour comes back instantly.
      </p>
      <p>
        OpenPowerlifting is open data and has been for years. We're built on top of their work and
        link back to their site throughout. None of this exists without them.
      </p>
    </section>

    <section class="prose-section">
      <h2 class="section-h">Statistical honesty</h2>
      <p>
        The Analysis tab runs Pearson correlation and pairwise linear regression and reports an
        effect-size-first verdict. With 3.9M rows, anything you regress will hit p &lt; 0.001, so
        framing a regression as "very significant" because of the p-value alone is misleading. We
        gate the verdict on |r| first ("negligible" if |r| &lt; 0.2 regardless of p), then on
        significance, then describe the result in plain English.
      </p>
      <p>
        Where a relationship is genuinely weak we say so, even when it's "statistically detectable."
        The point is to give you a useful read, rather than to inflate findings.
      </p>
    </section>

    <section class="prose-section">
      <h2 class="section-h">The roadmap</h2>
      <p>
        We plan to train our own machine learning models on this dataset and use AI to deliver
        genuinely valuable insights to lifters and coaches: career-trajectory forecasts, peer-group
        comparisons that take more than a handful of variables into account, programming-decision
        signals grounded in what actually predicts longevity and peak strength.
      </p>
      <p>
        The bar is statistical facts. Anything we ship will come with the same
        effect-size-first honesty we hold the existing regression cards to. If a model can't beat
        a sensible baseline, we won't dress it up as if it can.
      </p>
      <p>
        Longer-term we also plan to open up the API to developers (regression endpoints, cohort-
        filtered trends, career-arc data) for anyone building tooling on top of the dataset. If
        that's of interest, drop us a note and we'll prioritize accordingly.
      </p>
    </section>

    <section class="prose-section creator">
      <h2 class="section-h">About the creator</h2>
      <div class="creator-body">
        <p>
          Powerlifting Trends is built and maintained by a full-time platform software engineer
          and a powerlifter who's been in and around the sport for years. This site is a way to
          settle social media debates in real numbers, and a space to sharpen engineering skills
          on a problem space that's genuinely fun to work on while providing helpful knowledge
          to lifters in their career journeys.
        </p>
        <p>
          New side projects like this one are how I push myself to learn. Picking up tools and
          patterns I wouldn't touch in my day job, and finishing them all the way to a polished,
          public artifact rather than letting them rot in a sandbox folder.
        </p>
        <p>
          Catch up with me on Instagram at
          <a href="https://www.instagram.com/jbubz91/" target="_blank" rel="noopener" class="contact-email">@jbubz91</a>
          for the lifting side of things, or send a note to
          <a href="mailto:ideas@powerliftingtrends.com" class="contact-email">ideas@powerliftingtrends.com</a>
          for everything site-related: feature requests, data weirdness, partnerships, anything.
          Every email gets read.
        </p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.about {
  max-width: 768px;
  margin: 0 auto;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.about-hero {
  text-align: center;
  padding: 56px 24px 28px;
  background:
    radial-gradient(circle at 25% 30%, rgba(247, 129, 102, 0.14), transparent 50%),
    var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}
.about-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
}
.about-hero > * { position: relative; }
.hero-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 14px;
}
.about-hero h1 {
  font-size: 36px;
  line-height: 1.15;
  font-weight: 700;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
}
.hero-sub {
  max-width: 580px;
  margin: 0 auto;
  font-size: 15.5px;
  line-height: 1.6;
  color: var(--text-dim);
}

.section-h {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 14px;
  letter-spacing: -0.01em;
}

.prose-section p {
  font-size: 15px;
  line-height: 1.7;
  color: var(--text);
  margin: 0 0 14px;
}
.prose-section p:last-child { margin-bottom: 0; }
.prose-section a {
  color: var(--accent-2);
  text-decoration: none;
}
.prose-section a:hover { text-decoration: underline; }

.contact {
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 28px 28px;
  text-align: center;
}
.contact .section-h { margin-bottom: 8px; }
.contact p {
  max-width: 540px;
  margin: 0 auto;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--text-dim);
}
.contact-email {
  color: var(--accent);
  text-decoration: none;
  font-weight: 600;
}
.contact-email:hover { text-decoration: underline; }

@media (max-width: 768px) {
  .about { gap: 28px; }
  .about-hero { padding: 40px 18px 24px; }
  .about-hero h1 { font-size: 26px; }
  .hero-sub { font-size: 14px; }
  .section-h { font-size: 18px; }
  .prose-section p { font-size: 14.5px; }
  .contact { padding: 22px 20px; }
}
</style>
